import { formatItemsPerTransactionToBackend } from '@components/forms/utils/format-items-per-transaction-to-backend';
import { formatPriceToBackend } from '@components/forms/utils/format-price-to-backend';
import { type SupportedLanguages } from '@constants/constants.type';
import { ADMISSION_TYPE_BOTTLE_SERVICE, ADMISSION_TYPE_GUEST_LIST } from '@domain/admission';
import { type FormFieldPreset } from '@domain/form-field-presets';
import { type CreateAdmissionPresetRequest, type Preset } from '@domain/preset';
import { calculateDirittoPrevendita } from '@utils/calculateDirittoPrevendita/calculateDirittoPrevendita';

import { type AdmissionFormType, type AdmissionFormValue } from '../admission-form.type';
import { valuesEqual } from '../validation';

import { mapHolderGroupToBackend } from './form-fields';
import {
  applySettingsGranularity,
  isNominativeDirty,
  resolveTicketsLeftThreshold,
  toAccessCode,
  toBackendTranslations,
  toChannelSettings,
} from './to-backend';

export interface ToPresetBackendContext {
  value: AdmissionFormValue;
  type: Exclude<AdmissionFormType, 'pass'>;
  currency: string;
  mainLanguage: SupportedLanguages;
  companyClubId: string;
  preset?: Preset | null;
  defaultTicketsLeftThreshold: number;
  formFieldPresets: FormFieldPreset[];
}

const toBottleServicePresetFields = (
  basicInfo: AdmissionFormValue['basicInfo']
): Partial<CreateAdmissionPresetRequest> => {
  const extra = basicInfo.extraGuests;
  const extraGuestsOn = extra?.enabled ?? false;
  return {
    ...(basicInfo.guestsPerAdmission && { guestCapacity: Number(basicInfo.guestsPerAdmission) }),
    ...(extra !== undefined && {
      extraGuestMaxQuantity: extraGuestsOn && extra.maxQty ? Number(extra.maxQty) : null,
      pricePerExtraGuest: extraGuestsOn && extra.pricePerGuest ? Number(extra.pricePerGuest) : null,
    }),
  };
};

/**
 * Builds the full `CreateAdmissionPresetRequest` from the chassis form value. Presets are companyClub
 * templates: no event, so no sales schedule / status / entrance-time-slot; `italyRegulation` is the
 * top-level `{ dirittoPrevendita }` (the DDP field), not the SIAE block. Distribution maps like
 * admissions (BE accepts it on presets, B2B-1702). The drawer owns the endpoint split + dirty-diff.
 */
export const toCreatePresetRequest = ({
  value,
  type,
  currency,
  mainLanguage,
  companyClubId,
  preset,
  defaultTicketsLeftThreshold,
  formFieldPresets,
}: ToPresetBackendContext): CreateAdmissionPresetRequest => {
  const { basicInfo, advanced, appearance, vatRate, formFields, distribution } = value;
  const isCreate = !preset;

  const lowStock = advanced.lowStockThreshold;
  const drinks = advanced.drinks;
  const drinksReset = isCreate ? undefined : 0;
  const drinksIncluded = drinks?.enabled && drinks.quantity !== '' ? Number(drinks.quantity) : drinksReset;
  const { channels, isForAllChannels, channelDefaultMaxQuantity } = toChannelSettings(distribution.channelLimit);

  const request: CreateAdmissionPresetRequest = {
    ...(isCreate && { companyClubId, onlinePaymentPercentage: 100 }),
    price: formatPriceToBackend({ price: basicInfo.price, downPayment: basicInfo.downpayment, currency, type }),
    quantity: basicInfo.quantity ? Number(basicInfo.quantity) : null,
    itemsPerTransaction: formatItemsPerTransactionToBackend({
      min: basicInfo.itemsPerTransaction.min,
      max: basicInfo.itemsPerTransaction.max,
      quantity: Number(basicInfo.quantity),
      type,
    }),
    name: toBackendTranslations(basicInfo.title, mainLanguage, basicInfo.translationsEnabled),
    description: toBackendTranslations(basicInfo.description, mainLanguage, basicInfo.translationsEnabled),
    color: appearance.color ?? null,
    ...(type !== ADMISSION_TYPE_GUEST_LIST && { vatRate: vatRate.vatRate }),
    accessCode: toAccessCode(distribution.accessCode),
    channels,
    settings: {
      distribution: distribution.mode,
      isForAllChannels,
      channelDefaultMaxQuantity,
      isNominative: formFields.isNominative,
    },
    bookingFormFields: mapHolderGroupToBackend(formFields.mainHolder, preset?.bookingFormFields, formFieldPresets),
    ticketHolderFormFields: formFields.isNominative
      ? mapHolderGroupToBackend(formFields.ticketHolder, preset?.ticketHolderFormFields, formFieldPresets)
      : [],
    ...(drinksIncluded !== undefined && { drinksIncluded }),
    ...(lowStock && {
      ticketsLeftThreshold: resolveTicketsLeftThreshold(lowStock, defaultTicketsLeftThreshold, isCreate),
    }),
  };

  if (basicInfo.ddp !== undefined) {
    request.italyRegulation = { dirittoPrevendita: calculateDirittoPrevendita(basicInfo.ddp) };
  }

  if (type === ADMISSION_TYPE_BOTTLE_SERVICE) Object.assign(request, toBottleServicePresetFields(basicInfo));

  return request;
};

export const diffPresetRequest = (
  current: CreateAdmissionPresetRequest,
  baseline: CreateAdmissionPresetRequest
): CreateAdmissionPresetRequest => {
  const channelGroupDirty =
    !valuesEqual(current.channels, baseline.channels) ||
    !valuesEqual(current.settings?.isForAllChannels, baseline.settings?.isForAllChannels) ||
    !valuesEqual(current.settings?.channelDefaultMaxQuantity, baseline.settings?.channelDefaultMaxQuantity);

  const nominativeChanged = isNominativeDirty(current, baseline);
  const changed: CreateAdmissionPresetRequest = {};
  for (const key of Object.keys(current) as (keyof CreateAdmissionPresetRequest)[]) {
    const keep =
      key === 'channels'
        ? channelGroupDirty
        : (nominativeChanged && key === 'ticketHolderFormFields') || !valuesEqual(current[key], baseline[key]);
    if (keep) Object.assign(changed, { [key]: current[key] });
  }

  applySettingsGranularity(changed, current.settings, baseline.settings, false, channelGroupDirty);
  return changed;
};
